
export interface FormUpsertRequest extends Omit<Form, 'versionNumber' | 'createdAt' | 'fields'> {
  /** Will only be there when we are editing an existing form */
  formId?: string;
  fields: Array<Omit<FormField, 'id'>>;
  folderId?: string | null;
}

export interface FormResponse extends Form {
  formId: string;
  formVersionId: string;
  folderId?: string;
}

export type FormListItem = Omit<FormResponse, 'fields'>;
export interface FormListResponse {
  forms: FormListItem[];
}

export type FormType = "test" | "questionnaire" | "onboarding";

export interface Form {
  formType: FormType;
  title: string;
  description: string;
  versionNumber: number;
  fields: FormField[];
  createdAt: string;
}

export enum FieldType {
  Numeric = "numeric",
  ShortText = "shortText",
  Slider = "slider",
  Options = "options",
}

export type FormField = BaseField | NumericField | ShortTextField | SliderField | OptionsField;

export type FieldData = NumericField["data"] | ShortTextField["data"] | SliderField["data"] | OptionsField["data"];

export interface BaseField {
  title: string;
  description: string;
  required: boolean;
  fieldType: FieldType;
  data: FieldData;
  id: string;
}

export interface NumericField extends BaseField {
  fieldType: FieldType.Numeric;
  data: {};
}

export interface ShortTextField extends BaseField {
  fieldType: FieldType.ShortText;
  data: {};
}

export interface SliderField extends BaseField {
  fieldType: FieldType.Slider;
  data: {
    minValue: number;
    maxValue: number;
    minLabel: string;
    maxLabel: string;
    /** Whether to show steps or not */
    showSteps: boolean;
  };
}

export interface OptionsField extends BaseField {
  fieldType: FieldType.Options;
  data: {
    options: FormOption[];
    /**
     * If true, allows multiple selections in the form submitter
     */
    multi?: boolean;
  };
}

export interface FormOption {
  label: string;
  value: number;
}

export type FormSubmissionFieldData = Record<string, string | number | boolean | Array<number>>;
export interface FormSubmissionRequest {
  fieldData: FormSubmissionFieldData;
  formSubmissionId?: string;
}

export interface CreateFormSubmissionResponse {
  formSubmissionId: string;
}


export type BulkShareFormWithCustomerRequest = {
  formId: string;
  organizationCustomerIds: string[];
}

export type BulkShareFormWithGroupRequest = {
  formId: string;
  organizationGroupIds: string[];
}

export type GetSharedEntitiesResponse = {
  customers: Array<{
    id: string;
  }>;
  groups: Array<{
    id: string;
  }>;
}

export type SharedForm = {
  formId: string;
  formVersionId: string;
  versionNumber: number;
  title: string;
  description: string;
  totalFields: number;
  isArchived: boolean;
  displayOrder: number;
  formType: FormType;
}

export type GetSharedFormsResponse = {
  forms: SharedForm[];
}

export type ArchiveSharedFormRequest = {
  formIds: string[];
  isArchived: boolean;
};

export interface FormSubmission {
  fieldData: FormSubmissionFieldData;
  fieldDataForDisplay: Record<string, string>;
  createdAt: string;
  updatedAt: string;
  id: string;
  customer?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface FormSubmissionsResponse extends FormResponse {
  submissions: Array<FormSubmission>;
}

export interface FormSubmissionsWithCustomerResponse extends FormResponse {
  submissions: Array<Required<FormSubmission>>;
}

export type GetSubmissionResponse = {
  formVersions: FormSubmissionsResponse[];
}

export type GetFormSubmissionsResponse = {
  formVersions: FormSubmissionsWithCustomerResponse[];
}

export interface FormSubmissionResponse extends FormResponse {
  submission: FormSubmission;
}


export type UpdateFormOrderRequest = {
  formIds: string[];
}

export type BulkFormMoveToFolderRequest = {
  formIds: string[];
  folderId: string | null;
}
